import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { CheckIcon, CircleIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

function DropdownMenu({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot='dropdown-menu' {...props} />;
}

function DropdownMenuPortal({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Portal>) {
  return <DropdownMenuPrimitive.Portal data-slot='dropdown-menu-portal' {...props} />;
}

function DropdownMenuTrigger({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  return <DropdownMenuPrimitive.Trigger data-slot='dropdown-menu-trigger' {...props} />;
}

function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot='dropdown-menu-content'
        sideOffset={sideOffset}
        className={cn(
          'hc:bg-popover hc:text-popover-foreground hc:data-[state=open]:animate-in hc:data-[state=closed]:animate-out hc:data-[state=closed]:fade-out-0 hc:data-[state=open]:fade-in-0 hc:data-[state=closed]:zoom-out-95 hc:data-[state=open]:zoom-in-95 hc:data-[side=bottom]:slide-in-from-top-2 hc:data-[side=left]:slide-in-from-right-2 hc:data-[side=right]:slide-in-from-left-2 hc:data-[side=top]:slide-in-from-bottom-2 hc:z-50 hc:max-h-(--radix-dropdown-menu-content-available-height) hc:min-w-32 hc:origin-(--radix-dropdown-menu-content-transform-origin) hc:overflow-x-hidden hc:overflow-y-auto hc:rounded-md hc:border hc:p-1 hc:shadow-md',
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
}

function DropdownMenuGroup({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  return <DropdownMenuPrimitive.Group data-slot='dropdown-menu-group' {...props} />;
}

function DropdownMenuItem({
  className,
  inset,
  variant = 'default',
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
  variant?: 'default' | 'destructive';
}) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot='dropdown-menu-item'
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "hc:focus:bg-accent hc:focus:text-accent-foreground hc:data-[variant=destructive]:text-destructive hc:data-[variant=destructive]:focus:bg-destructive/10 hc:dark:data-[variant=destructive]:focus:bg-destructive/20 hc:data-[variant=destructive]:focus:text-destructive hc:data-[variant=destructive]:*:[svg]:text-destructive! hc:[&_svg:not([class*='text-'])]:text-muted-foreground hc:relative hc:flex hc:cursor-default hc:items-center hc:gap-2 hc:rounded-sm hc:px-2 hc:py-1.5 hc:text-sm hc:outline-hidden hc:select-none hc:data-disabled:pointer-events-none hc:data-disabled:opacity-50 hc:data-inset:pl-8 hc:[&_svg]:pointer-events-none hc:[&_svg]:shrink-0 hc:[&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  );
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot='dropdown-menu-checkbox-item'
      className={cn(
        "hc:focus:bg-accent hc:focus:text-accent-foreground hc:relative hc:flex hc:cursor-default hc:items-center hc:gap-2 hc:rounded-sm hc:py-1.5 hc:pr-2 hc:pl-8 hc:text-sm hc:outline-hidden hc:select-none hc:data-disabled:pointer-events-none hc:data-disabled:opacity-50 hc:[&_svg]:pointer-events-none hc:[&_svg]:shrink-0 hc:[&_svg:not([class*='size-'])]:size-4",
        className
      )}
      checked={checked}
      {...props}>
      <span className='hc:pointer-events-none hc:absolute hc:left-2 hc:flex hc:size-3.5 hc:items-center hc:justify-center'>
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon className='size-4' />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

function DropdownMenuRadioGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) {
  return <DropdownMenuPrimitive.RadioGroup data-slot='dropdown-menu-radio-group' {...props} />;
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot='dropdown-menu-radio-item'
      className={cn(
        "hc:focus:bg-accent hc:focus:text-accent-foreground hc:relative hc:flex hc:cursor-default hc:items-center hc:gap-2 hc:rounded-sm hc:py-1.5 hc:pr-2 hc:pl-8 hc:text-sm hc:outline-hidden hc:select-none hc:data-disabled:pointer-events-none hc:data-disabled:opacity-50 hc:[&_svg]:pointer-events-none hc:[&_svg]:shrink-0 hc:[&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}>
      <span className='hc:pointer-events-none hc:absolute hc:left-2 hc:flex hc:size-3.5 hc:items-center hc:justify-center'>
        <DropdownMenuPrimitive.ItemIndicator>
          <CircleIcon className='hc:size-2 hc:fill-current' />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  );
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
}) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot='dropdown-menu-label'
      data-inset={inset}
      className={cn('hc:px-2 hc:py-1.5 hc:text-sm hc:font-medium hc:data-inset:pl-8', className)}
      {...props}
    />
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot='dropdown-menu-separator'
      className={cn('hc:bg-border hc:-mx-1 hc:h-px my-1', className)}
      {...props}
    />
  );
}

function DropdownMenuShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot='dropdown-menu-shortcut'
      className={cn('hc:text-muted-foreground hc:ml-auto hc:text-xs hc:tracking-widest', className)}
      {...props}
    />
  );
}

function DropdownMenuSub({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
  return <DropdownMenuPrimitive.Sub data-slot='dropdown-menu-sub' {...props} />;
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean;
}) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot='dropdown-menu-sub-trigger'
      data-inset={inset}
      className={cn(
        'hc:focus:bg-accent hc:focus:text-accent-foreground hc:data-[state=open]:bg-accent hc:data-[state=open]:text-accent-foreground hc:flex hc:cursor-default hc:items-center hc:rounded-sm hc:px-2 hc:py-1.5 hc:text-sm hc:outline-hidden hc:select-none hc:data-inset:pl-8',
        className
      )}
      {...props}>
      {children}
    </DropdownMenuPrimitive.SubTrigger>
  );
}

function DropdownMenuSubContent({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  return (
    <DropdownMenuPrimitive.SubContent
      data-slot='dropdown-menu-sub-content'
      className={cn(
        'hc:bg-popover hc:text-popover-foreground hc:data-[state=open]:animate-in hc:data-[state=closed]:animate-out hc:data-[state=closed]:fade-out-0 hc:data-[state=open]:fade-in-0 hc:data-[state=closed]:zoom-out-95 hc:data-[state=open]:zoom-in-95 hc:data-[side=bottom]:slide-in-from-top-2 hc:data-[side=left]:slide-in-from-right-2 hc:data-[side=right]:slide-in-from-left-2 hc:data-[side=top]:slide-in-from-bottom-2 hc:z-50 hc:min-w-32 hc:origin-(--radix-dropdown-menu-content-transform-origin) hc:overflow-hidden hc:rounded-md hc:border hc:p-1 hc:shadow-lg',
        className
      )}
      {...props}
    />
  );
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
};
